/**
 * Ein einzelner Tauchgang: Profil oben, Zahlen darunter.
 *
 * Reihenfolge nach dem, was zaehlt: erst die Kurve, dann die Kennzahlen, dann
 * Bedingungen, Ausruestung und Notizen. Leere Felder werden nicht angezeigt —
 * ein Tauchgang von 2001 hat nun einmal keine Herzfrequenz.
 */

import { hatDeko, zeichneProfil } from '../chart.js';
import { SPEICHER, messpunkte, stammsatz } from '../db.js';
import { speichere } from '../saetze.js';
import { datum, dauer, gerundet, koordinaten, kurzfassung, pause, temperatur, tiefe, uhrzeit, woerter, wort, zahl } from '../format.js';
import { istSchmal } from '../mobil.js';
import { alsBlatt } from './blatt.js';
import { gewaesserAus, landAus, ortsangabe } from '../orte.js';
import { tiefenfarbe } from '../tiefenfarbe.js';
import { ansichtDetailkarte } from './detailkarte.js';
import { kennzahl } from './kennzahl.js';

function feld(bezeichnung, wert) {
  if (wert === null || wert === undefined || wert === '' || wert === '—') return '';
  return `<div class="feld"><dt>${bezeichnung}</dt><dd>${wert}</dd></div>`;
}

/**
 * Ein Wert, der nicht gepflegt, sondern aus den Koordinaten gerechnet ist.
 *
 * Die Marke ist keine Zierde: Gepflegtes und Abgeleitetes dürfen nicht gleich
 * aussehen, sonst hält man die Schätzung irgendwann für eine Eingabe. Gespeichert
 * wird sie ohnehin nie — sie steht bei jedem Aufruf neu da.
 */
function abgeleitet(wert) {
  if (!wert) return null;
  return `${wert} <span class="abgeleitet" title="aus den Koordinaten abgeleitet, `
    + `nicht gespeichert">abgeleitet</span>`;
}

/**
 * Woher die Koordinaten stammen. Von Hand gesetzt ist keine Messung — wer die
 * Position später prüft, soll sehen, worauf er sich verlässt.
 *
 * Nicht `herkunft` nennen: So heisst weiter unten die Quellenzeile des
 * Tauchgangs (`const herkunft`). Der lokale Name beschattete die Funktion im
 * ganzen Rumpf, und der Aufruf hier oben lief in dessen temporale Todeszone —
 * die Detailseite blieb leer.
 */
function positionsmarke(platz) {
  if (platz?.geocode_source !== 'manual') return '';
  return ' <span class="herkunftsmarke" title="Position manuell hinzugefügt">von Hand</span>';
}

/**
 * Wie viel Gas weg ist — als Kennzahl, nicht als siebte Zeile im Datenblatt.
 *
 * Der Druck stand nur in „Ausruestung", dort an Position sieben von acht und
 * damit hinter einem Tipp und sechs Zeilen Neopren. Fuer einen Taucher ist er
 * eine Kopfzahl: Was der Tauchgang gekostet hat, sieht man daran und an sonst
 * nichts. Der Verbrauch steht gross, Anfang und Ende klein darunter — die
 * Differenz ist die Auskunft, die beiden Werte sind der Beleg.
 */
function druckKennzahl(tg) {
  const flasche = (tg.tanks ?? [])[0] ?? {};
  const { start_bar: anfang, end_bar: ende } = flasche;
  if (typeof anfang !== 'number' || typeof ende !== 'number') return '';
  return kennzahl(gerundet(anfang - ende, 'bar'), 'Verbrauch', {
    zusatz: `${gerundet(anfang)} → ${gerundet(ende, 'bar')}`,
    zeichen: 'flasche',
  });
}

/**
 * Ein Block: am Schreibtisch ein Abschnitt, am Telefon eine Kachel.
 *
 * Ausgefahren waren die fuenf Bloecke zusammen 1824 Pixel — 36 Paare aus
 * Beschriftung und Wert, immer alle, immer sichtbar. Auf einem Telefon ist das
 * keine Auskunft mehr, sondern eine Wand, an der man vorbeiblaettert. Aus dem
 * Abschnitt wird dort deshalb eine Kachel mit Kurzfassung (`zuKacheln()`).
 */
function block(titel, inhalt) {
  if (!inhalt.trim()) return '';
  return `<section class="block" data-titel="${titel}"><h2>${titel}</h2>`
    + `<dl>${inhalt}</dl></section>`;
}

/** Die Werte eines Blocks, ohne die Marken, die am Wert haengen. */
function werteAus(abschnitt) {
  return [...abschnitt.querySelectorAll('.feld dd')].map((dd) => {
    const kopie = dd.cloneNode(true);
    // „abgeleitet" und „von Hand" sind Marken am Wert, nicht der Wert.
    for (const marke of kopie.querySelectorAll('.abgeleitet, .herkunftsmarke')) marke.remove();
    return kopie.textContent;
  });
}

/**
 * Auf dem Telefon wird aus einem Block eine Kachel, die ein Blatt oeffnet.
 *
 * Ein Ausklapper an dieser Stelle war der falsche Griff: Die aufgehende Kachel
 * nahm beide Spalten, alle anderen sprangen weg, und zum Schliessen musste man
 * dieselbe Kopfzeile treffen, die inzwischen woanders stand. Ein Blatt hat
 * genau einen Weg hinein und drei hinaus — Knopf, Wisch nach unten, Escape —
 * und laesst das Raster in Ruhe.
 */
function zuKacheln(anhang) {
  for (const abschnitt of anhang.querySelectorAll('.block:not(.block-offen)')) {
    const titel = abschnitt.dataset.titel ?? '';
    const liste = abschnitt.querySelector('dl');
    if (!liste) continue;

    const kachel = document.createElement('button');
    kachel.type = 'button';
    kachel.className = 'kachel';
    kachel.innerHTML = `<span class="kachel-titel">${titel}</span>`;
    const kurz = document.createElement('span');
    kurz.className = 'kachel-kurz';
    kurz.textContent = kurzfassung(werteAus(abschnitt));
    kachel.append(kurz);

    kachel.addEventListener('click', () => oeffneBlatt(titel, liste.cloneNode(true)));
    abschnitt.replaceWith(kachel);
  }
}

/** Das Blatt zu einer Kachel: Titel, die Tabelle, ein Weg hinaus. */
function oeffneBlatt(titel, liste) {
  const dialog = document.createElement('dialog');
  dialog.className = 'blattdialog kachelblatt';
  dialog.innerHTML = `<header class="kachelblatt-kopf"><h2>${titel}</h2>`
    + '<button type="button" class="kachelblatt-zu" aria-label="Schliessen">✕</button></header>';
  dialog.append(liste);
  alsBlatt(dialog);
  dialog.querySelector('.kachelblatt-zu').addEventListener('click', () => dialog.close());
  // Ein Tipp neben das Blatt schliesst es — bei <dialog> trifft man dabei das
  // Element selbst, nicht den Hintergrund dahinter.
  dialog.addEventListener('click', (e) => { if (e.target === dialog) dialog.close(); });
  dialog.addEventListener('close', () => dialog.remove());
  document.body.append(dialog);
  dialog.showModal();
}

export async function ansichtDetail(db, tg, aufAenderung = null) {
  const wurzel = document.createElement('article');
  wurzel.className = 'detail';
  if (!tg) {
    wurzel.innerHTML = '<p class="leer">Diesen Tauchgang gibt es nicht. '
      + '<a href="#/">Zurück zur Liste</a></p>';
    return wurzel;
  }

  const platz = await stammsatz(db, SPEICHER.sites, tg.site_id);
  const reise = await stammsatz(db, SPEICHER.trips, tg.trip_id);
  const werte = tg.has_profile ? await messpunkte(db, tg.id) : null;

  wurzel.innerHTML = `
    <header class="detail-kopf">
      <a class="zurueck" href="#/">← Liste</a>
      <a class="knopf bearbeiten" data-kopfaktion href="#/bearbeiten/${tg.id}">Bearbeiten</a>
      <p class="detail-nummer">Tauchgang ${tg.dive_number ?? '—'}</p>
      <h1></h1>
      <p class="detail-zeit">${datum(tg)} · ${uhrzeit(tg)}${
        tg.tz_offset_min !== null && tg.tz_offset_min !== undefined
          ? ` <span class="leise">Ortszeit</span>` : ''}</p>
    </header>
    <div class="kennzahlen">
      ${kennzahl(tiefe(tg.max_depth_m), 'Maximaltiefe', {
    zeichen: 'tiefe',
    klasse: 'gross',
    farbe: typeof tg.max_depth_m === 'number' ? tiefenfarbe(tg.max_depth_m) : '',
  })}
      ${kennzahl(dauer(tg.duration_s), 'Grundzeit', { zeichen: 'zeit' })}
      ${kennzahl(tiefe(tg.avg_depth_m), 'Ø Tiefe', { zeichen: 'schnitt' })}
      ${kennzahl(temperatur(tg.water_temp_min_c ?? tg.water_temp_avg_c), 'Wasser',
    { zeichen: 'temperatur' })}
      ${druckKennzahl(tg)}
    </div>
    <div class="profil" id="profil"></div>`;

  // Der Name kommt aus dem Bundle; fehlt er, hilft der Stammsatz weiter.
  wurzel.querySelector('h1').textContent = tg.site_name ?? platz?.name ?? 'ohne Platz';

  // Profil und Karte bringen je eine eigene Aufraeumfunktion mit; der Router
  // ruft nur eine. Ohne die Sammlung bliebe die zuletzt gesetzte uebrig und die
  // andere haenge weiter am Fenster.
  const aufraeumer = [];
  wurzel.aufraeumen = () => { for (const f of aufraeumer.splice(0)) f(); };

  const profilbereich = wurzel.querySelector('#profil');
  if (werte && werte.length > 0) {
    requestAnimationFrame(() => {
      const { aufraeumen, daten } = zeichneProfil(profilbereich, werte, tg.max_depth_m);
      aufraeumer.push(aufraeumen);
      // Eine rote Flaeche ohne Erklaerung ist ein Schreck, keine Auskunft —
      // und ohne Deko waere die Legende eine Erklaerung fuer nichts.
      if (hatDeko(daten[1])) {
        const legende = document.createElement('p');
        legende.className = 'profil-legende';
        legende.textContent = 'Rot: Dekostopp-Bereich — so flach stand die Decke '
          + 'des Tauchcomputers. Aufgezeichnet, nicht gerechnet.';
        profilbereich.after(legende);
      }
    });
  } else if (tg.has_profile) {
    // Seit dem Geraeteabgleich kann ein Tauchgang hier ankommen, ohne dass
    // seine Messpunkte mitgewandert sind — sie machen 95 Prozent der Datenmenge
    // aus und bleiben deshalb draussen. Ohne diesen Satz stuende hier nichts.
    profilbereich.innerHTML = '<p class="profil-fehlt">Das Tiefenprofil liegt auf '
      + 'diesem Gerät nicht vor. Es kommt mit dem Bundle aus <code>out/export/</code>.</p>';
  } else {
    profilbereich.innerHTML = '<p class="kein-profil">Kein Tiefenprofil aufgezeichnet — '
      + 'die Kopfdaten stammen aus dem Logbuch.</p>';
  }

  // Land und Gewässer fehlen bei den meisten Alt-Tauchgängen. Liegen
  // Koordinaten vor — vom Garmin-GPS oder von Hand über „Verorten" —, lassen
  // sich beide aus der Tabelle in `orte.js` ableiten, ohne einen Dienst zu
  // fragen. Gepflegte Werte gehen immer vor.
  const punkt = ortsangabe(tg, platz);
  const land = punkt ? landAus(punkt.lat, punkt.lon) : null;
  const gewaesser = punkt ? gewaesserAus(punkt.lat, punkt.lon) : null;

  const ort = [
    feld('Region', platz?.region),
    feld('Land', platz?.country || tg.country || abgeleitet(land?.name)),
    feld('Koordinaten', koordinaten(platz?.lat, platz?.lon)
      ? koordinaten(platz.lat, platz.lon) + positionsmarke(platz) : null),
    feld('Gewässer', platz?.water_name || abgeleitet(gewaesser)),
    feld('Reise', reise?.name),
    feld('Boot', tg.boat ?? reise?.boat),
    feld('Basis', reise?.shop),
    feld('Ort', tg.city),
  ].join('');

  const bedingungen = [
    feld('Wasser', wort(tg.water_type)),
    feld('Einstieg', wort(tg.entry_type)),
    feld('Strömung', wort(tg.current)),
    feld('Oberfläche', wort(tg.surface_cond)),
    feld('Wetter', tg.weather),
    feld('Sicht', tg.visibility_m ? tiefe(tg.visibility_m) : wort(tg.visibility_class)),
    feld('Lufttemperatur', temperatur(tg.air_temp_c)),
    feld('Art', woerter(tg.dive_type)),
  ].join('');

  const flasche = (tg.tanks ?? [])[0] ?? {};
  // Das Gas zuerst: Es aendert sich von Tauchgang zu Tauchgang, der Anzug
  // nicht. Die Kurzfassung der Kachel nimmt die ersten Werte — sie soll von
  // dem reden, was diesen Tauchgang ausmacht, nicht von Neopren.
  const ausruestung = [
    feld('Druck', typeof flasche.start_bar === 'number'
      ? `${gerundet(flasche.start_bar)} → ${gerundet(flasche.end_bar, 'bar')}` : null),
    feld('Gas', wort(flasche.gas_type)),
    feld('Sauerstoff', flasche.o2_pct ? `${flasche.o2_pct} %` : null),
    feld('Flasche', flasche.volume_l ? `${flasche.volume_l} l ${wort(flasche.tank_type) ?? ''}` : null),
    feld('Verbrauch', gerundet(tg.rmv_l_min, 'l/min')),
    feld('Anzug', [wort(tg.suit_type), tg.suit_thickness_mm ? `${tg.suit_thickness_mm} mm` : null]
      .filter(Boolean).join(' · ')),
    feld('Blei', zahl(tg.weight_kg, 'kg', 1)),
    feld('Gerät', tg.source_device),
  ].join('');

  const koerper = [
    feld('Herzfrequenz', tg.hr_avg_bpm ? `Ø ${tg.hr_avg_bpm} · max ${tg.hr_max_bpm} bpm` : null),
    feld('Stickstoff', tg.n2_load_end_pct !== null && tg.n2_load_end_pct !== undefined
      ? `${tg.n2_load_start_pct ?? 0} % → ${tg.n2_load_end_pct} %` : null),
    feld('ZNS', tg.cns_end_pct !== null && tg.cns_end_pct !== undefined
      ? `${tg.cns_start_pct ?? 0} % → ${tg.cns_end_pct} %` : null),
    feld('Oberflächenpause', pause(tg.surface_interval_s)),
    feld('Dekomodell', tg.deco_model ? `${tg.deco_model} (GF ${tg.gf_low}/${tg.gf_high})` : null),
  ].join('');

  const karte = ansichtDetailkarte(tg, platz);
  if (karte) {
    wurzel.append(karte);
    aufraeumer.push(() => karte.aufraeumen?.());
  }

  const anhang = document.createElement('div');
  anhang.className = 'bloecke';
  anhang.innerHTML = block('Ort', ort) + block('Bedingungen', bedingungen)
    + block('Ausrüstung', ausruestung) + block('Messwerte', koerper);
  wurzel.append(anhang);

  // `buddy_raw` kommt aus dem Import, `buddies` aus dem Formular der App. Wer
  // nur das erste liest, zeigt keinen einzigen selbst erfassten Buddy an —
  // genau das war der Fall.
  const buddyText = tg.buddy_raw
    || (Array.isArray(tg.buddies) ? tg.buddies.filter(Boolean).join(', ') : '');
  if (buddyText) {
    const buddys = document.createElement('section');
    buddys.className = 'block block-offen';
    buddys.innerHTML = '<h2>Buddys</h2>';
    const p = document.createElement('p');
    p.className = 'buddys';
    p.textContent = buddyText;
    buddys.append(p);
    anhang.append(buddys);
  }

  if (tg.notes) {
    const notiz = document.createElement('section');
    notiz.className = 'block block-offen notiz';
    notiz.innerHTML = '<h2>Notizen</h2>';
    const p = document.createElement('p');
    p.textContent = tg.notes;
    notiz.append(p);
    anhang.append(notiz);
  }

  // Eine Marke, die man nicht loswird, ist keine Aufgabe, sondern ein Makel:
  // Der Import setzt „zu pruefen", die Suche filtert danach, die Statistik
  // zaehlt es — nur wegnehmen konnte es niemand. Wer nachgesehen hat, hakt es
  // hier ab; der Satz geht ueber denselben Schreibweg wie alles andere und
  // wandert damit auch in Abgleich und Abzug.
  if (tg.needs_review) {
    const hinweis = document.createElement('div');
    hinweis.className = 'pruefhinweis';
    const text = document.createElement('p');
    text.className = 'pruefhinweis-text';
    text.textContent = `Zu prüfen: ${tg.review_note ?? ''}`.trim();
    const erledigt = document.createElement('button');
    erledigt.type = 'button';
    erledigt.className = 'knopf pruefhinweis-erledigt';
    erledigt.textContent = 'Geprüft';
    erledigt.addEventListener('click', async () => {
      erledigt.disabled = true;
      try {
        await speichere(db, SPEICHER.dives,
          { ...tg, needs_review: 0, review_note: null });
        hinweis.remove();
        await aufAenderung?.();
      } catch (fehler) {
        erledigt.disabled = false;
        text.textContent = `Nicht gespeichert: ${fehler.message}`;
      }
    });
    hinweis.append(text, erledigt);
    anhang.append(hinweis);
  }

  if (istSchmal()) zuKacheln(anhang);

  const herkunft = document.createElement('p');
  herkunft.className = 'herkunft';
  herkunft.textContent = `Quelle: ${tg.source ?? '—'}`
    + (tg.profile_samples ? ` · ${tg.profile_samples} Messpunkte` : '');
  anhang.append(herkunft);

  return wurzel;
}
